import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Lock, MailPlus, Trash2, UserCheck, UserX } from "lucide-react"
import type * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { IconButton, TooltipContent } from "@/components/ui/icon-button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDateShort, formatRelative } from "@/lib/format-date"
import type { Role } from "@/lib/roles"
import { cn } from "@/lib/utils"

import {
  accountStatus,
  ROLE_OPTIONS,
  type AccountStatus,
  type ConsoleUser,
  type ServiceKey,
} from "./users-keys-data"

// ── Users ────────────────────────────────────────────────────────────

export function UserAccountCell({
  user,
  isSelf,
}: {
  user: ConsoleUser
  isSelf: boolean
}): JSX.Element {
  const status = accountStatus(user)
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={user.name ?? user.email} identity={user.id} size="md" />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-body font-medium text-text">
            {user.name ?? user.email}
          </span>
          {/* "You" replaces the old "Bootstrap" pill, which guessed by email address. Your own
              row is the one this screen will not let you change. */}
          {isSelf && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-surface-sunken px-2 py-0.5 text-caption font-medium text-text-2 ring-1 ring-inset ring-border">
              You
            </span>
          )}
          <StatusPill status={status} />
        </div>
        {user.name && (
          <div className="mt-0.5 truncate font-id text-caption text-text-3">{user.email}</div>
        )}
      </div>
    </div>
  )
}

// Every account carries a pill, so the state is readable on every row rather than implied
// by a missing badge. Green for active, amber for a pending invite, muted for deactivated.
const STATUS_PILL: Record<AccountStatus, { label: string; classes: string }> = {
  active: { label: "Active", classes: "bg-master-tint text-master ring-master/20" },
  "pending-invite": { label: "Invited", classes: "bg-print-tint text-print ring-print/20" },
  deactivated: { label: "Deactivated", classes: "bg-surface-sunken text-text-3 ring-border" },
}

function StatusPill({ status }: { status: AccountStatus }): JSX.Element {
  const { label, classes } = STATUS_PILL[status]
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-caption font-medium ring-1 ring-inset ${classes}`}
    >
      {label}
    </span>
  )
}

export function RoleCell({
  user,
  onChange,
  disabled,
}: {
  user: ConsoleUser
  onChange: (role: Role) => void
  disabled: boolean
}): JSX.Element {
  return (
    <Select
      value={user.role}
      onValueChange={(v) => {
        onChange(v as Role)
      }}
      disabled={disabled}
    >
      <SelectTrigger selectSize="sm" className="w-[260px]" aria-label={`Role for ${user.email}`}>
        <SelectValue />
      </SelectTrigger>
      {/* No width override: the shared SelectContent already matches the trigger. */}
      <SelectContent align="start">
        {ROLE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} description={opt.description}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Account creation date. The backend records no logins, so this replaces "Last seen". */
export function JoinedCell({ iso }: { iso: string }): JSX.Element {
  return (
    <span className="font-id tabular-nums text-body-sm text-text-3" title={iso}>
      {formatDateShort(iso)}
    </span>
  )
}

export interface UserRowActions {
  onResendInvite: () => void
  onSetActive: (active: boolean) => void
  /** DELETE /users/{id}. Worded "Cancel invite" for an invitation nobody has accepted. */
  onDelete: () => void
}

/**
 * The Actions column: the row's commands, always visible, drawn like menu items.
 *
 *   pending invite  → Resend invite · Cancel invite
 *   everyone else   → Deactivate / Reactivate · Delete user
 *
 * Your own row gets a lock instead: an admin cannot deactivate or delete themselves here.
 */
export function UserActionsCell({
  user,
  isSelf,
  busy,
  actions,
}: {
  user: ConsoleUser
  isSelf: boolean
  busy: boolean
  actions: UserRowActions
}): JSX.Element {
  if (isSelf) {
    return (
      <span
        title="You can't change your own account here."
        className="inline-flex items-center text-text-3"
      >
        <Lock className="size-4" aria-hidden="true" />
        <span className="sr-only">You can&apos;t change your own account here.</span>
      </span>
    )
  }

  if (accountStatus(user) === "pending-invite") {
    return (
      <div className="flex items-center justify-center gap-1">
        <ActionItem
          icon={<MailPlus />}
          tooltip="Sends a fresh link; earlier ones stop working."
          disabled={busy}
          onClick={actions.onResendInvite}
        >
          Resend Invite
        </ActionItem>
        {/* Not "Deactivate": deactivating a pending invite cancels it for good and leaves an
            account nobody can sign in to. Removing it is the honest version. */}
        <ActionItem
          icon={<Trash2 />}
          destructive
          tooltip="Removes the account; the link in their email stops working."
          disabled={busy}
          onClick={actions.onDelete}
        >
          Cancel Invite
        </ActionItem>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center gap-1">
      {user.isActive ? (
        <ActionItem
          icon={<UserX />}
          tooltip="Signs them out and blocks sign-in. You can reactivate them later."
          disabled={busy}
          onClick={() => {
            actions.onSetActive(false)
          }}
        >
          Deactivate
        </ActionItem>
      ) : (
        <ActionItem
          icon={<UserCheck />}
          tooltip="Lets them sign in again."
          disabled={busy}
          onClick={() => {
            actions.onSetActive(true)
          }}
        >
          Reactivate
        </ActionItem>
      )}
      <ActionItem
        icon={<Trash2 />}
        destructive
        tooltip="Permanent. Can't be undone."
        disabled={busy}
        onClick={actions.onDelete}
      >
        Delete User
      </ActionItem>
    </div>
  )
}

/**
 * A row command drawn like a DropdownMenuItem (icon and label; red when destructive), but as
 * a plain button that is always on screen. What it does goes in a hover/focus tooltip, the
 * shared TooltipContent; the shell already mounts the tooltip provider.
 */
function ActionItem({
  icon,
  tooltip,
  destructive = false,
  disabled = false,
  onClick,
  children,
}: {
  icon: React.ReactNode
  tooltip: string
  destructive?: boolean
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={cn(
            "flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 outline-none",
            "text-body-sm transition-colors duration-120 ease-out",
            "focus-visible:ring-2 focus-visible:ring-brand/40",
            "disabled:pointer-events-none disabled:opacity-45",
            destructive ? "text-err hover:bg-err-tint" : "text-text hover:bg-surface-sunken",
          )}
        >
          <span className="shrink-0 text-text-3 [&_svg]:size-3.5" aria-hidden="true">
            {icon}
          </span>
          <span>{children}</span>
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipContent side="top" className="max-w-64">
        {tooltip}
      </TooltipContent>
    </TooltipPrimitive.Root>
  )
}

// ── Service keys ─────────────────────────────────────────────────────

export function KeyCell({ item }: { item: ServiceKey }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate text-body font-medium text-text">{item.name}</span>
        {item.fromEnv && (
          <span
            title="Seeded by PYXIS_API_KEYS at boot"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-surface-sunken px-2 py-0.5 text-caption font-medium text-text-2 ring-1 ring-inset ring-border"
          >
            <Lock className="size-3" aria-hidden="true" />
            from env
          </span>
        )}
      </div>
      <div className="mt-0.5 truncate font-id text-caption text-text-3">
        {item.prefix}
        {"·".repeat(20)}
      </div>
    </div>
  )
}

export function CreatedCell({ item }: { item: ServiceKey }): JSX.Element {
  return (
    <div className="min-w-0 text-body-sm">
      <div className="font-id tabular-nums text-text-2">{formatRelative(item.createdAt)}</div>
      <div className="mt-0.5 truncate text-caption text-text-3">by {item.createdBy}</div>
    </div>
  )
}

export function LastUsedCell({ iso }: { iso: string | null }): JSX.Element {
  if (iso === null) return <span className="text-body-sm text-text-3">Never used</span>
  return <span className="font-id tabular-nums text-body-sm text-text">{formatRelative(iso)}</span>
}

export function RevokeKeyCell({
  item,
  onRevoke,
}: {
  item: ServiceKey
  onRevoke: () => void
}): JSX.Element {
  if (item.fromEnv) {
    return (
      <span
        title="Remove from PYXIS_API_KEYS and restart to revoke."
        className="inline-flex items-center text-text-3"
      >
        <Lock className="size-4" aria-hidden="true" />
      </span>
    )
  }
  return (
    <IconButton
      size="sm"
      variant="ghost"
      label={`Revoke ${item.name}`}
      icon={<Trash2 />}
      onClick={onRevoke}
    />
  )
}
